import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { adsApi, campaignsApi, flagsApi } from '@/api/client';
import { automationApi } from '@/api/automationApi';
import type { CampaignAdProduct } from '@/types/spCampaigns';
import type { HourlyBudgetListItem } from '@/types/hourlyBudget';
import type { FlagPreviewRequest } from '@/types/automationFlags';
import type { Portfolio } from '@/types/portfolios';
import {
  mapEntityApiItems,
  type AutomationCampaignOption,
} from '@/components/automation/automationCampaignScope';
import { FlagCallout } from '@/components/flags/FlagCallout';
import { FlagBadge } from '@/components/flags/FlagBadge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Table, TableWrap, TableHeader, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { useToast } from '@/components/ui/Toast';
import { DaypartingStatus } from '@/components/hourlyBudget/DaypartingList';
import {
  DaypartingScopePicker,
  filterCampaignsByPortfolio,
  type DaypartingScopeType,
} from '@/components/hourlyBudget/DaypartingScopePicker';
import { HourlyBudgetWeekGrid } from '@/components/hourlyBudget/HourlyBudgetWeekGrid';
import { WorkspaceTablePagination } from '@/components/workspace/WorkspaceSection';
import { useServerPagination, totalPages } from '@/hooks/useServerPagination';
import { hasErrorFlags, parseAutomationFlags } from '@/utils/automationFlags';
import {
  configuredHourlyBudgetCampaignIds,
  formatOperatingWindows,
  hourlyBudgetCampaigns,
  hourlyBudgetGroupId,
  hourlyBudgetProductsLabel,
  hourlyBudgetScopeLabel,
  profileHourlyBudgetsQueryKey,
} from '@/utils/hourlyBudgetList';
import { canToggleHourOn } from '@/utils/hourlyBudgetHourSlots';
import {
  WEEKDAYS,
  emptyWeeklyAmounts,
  emptyWeeklySlots,
  expandWindowsToWeeklySlots,
  type WeekdayIndex,
} from '@/utils/hourlyBudgetWeek';
import type { HourSlotMap } from '@/utils/hourlyBudgetHourSlots';
import {
  BID_METHOD_OPTIONS,
  PLACEMENT_FIELD_OPTIONS,
  PLACEMENT_MODE_OPTIONS,
  buildBidPlacementGroupPayload,
  placementActionFromRows,
  selectedHourCount,
  summarizeBidAction,
  summarizePlacementAction,
  validateBidAction,
  validatePlacementRows,
  type BidDaypartingAction,
  type BidMethod,
  type PlacementMode,
} from '@/utils/bidPlacementDayparting';

type Kind = 'bid' | 'placement';

type PlacementRows = Record<string, { mode: PlacementMode; value: string }>;

const COPY: Record<Kind, { noun: string; add: string; empty: string; change: string }> = {
  bid: {
    noun: 'bid dayparting',
    add: 'Add bid schedule',
    empty: 'No campaigns have bid dayparting',
    change: 'Change',
  },
  placement: {
    noun: 'placement dayparting',
    add: 'Add placement schedule',
    empty: 'No campaigns have placement dayparting',
    change: 'Change',
  },
};

function parsePortfolios(data: unknown): Portfolio[] {
  if (!data || typeof data !== 'object') return [];
  const root = data as Record<string, unknown>;
  const list = Array.isArray(root.portfolios) ? root.portfolios : [];
  return list.filter((item): item is Portfolio => !!item && typeof item === 'object' && 'portfolioId' in item);
}

function apiErrorMessage(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { detail?: unknown } } };
  const detail = ax.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function emptyPlacementRows(): PlacementRows {
  return {
    TOP_OF_SEARCH: { mode: 'SET', value: '' },
    REST_OF_SEARCH: { mode: 'SET', value: '' },
    PRODUCT_PAGES: { mode: 'SET', value: '' },
  };
}

function bidFormFromAction(action: unknown): { method: BidMethod; value: string } {
  if (action && typeof action === 'object' && 'method' in action) {
    const row = action as { method?: string; value?: unknown };
    const method = BID_METHOD_OPTIONS.some((option) => option.id === row.method)
      ? (row.method as BidMethod)
      : 'increase_pct';
    return { method, value: row.value == null ? '' : String(row.value) };
  }
  return { method: 'increase_pct', value: '' };
}

function placementRowsFromAction(action: unknown): PlacementRows {
  const rows = emptyPlacementRows();
  if (!action || typeof action !== 'object' || !('adjustments' in action)) return rows;
  const adjustments = (action as { adjustments?: unknown }).adjustments;
  if (!Array.isArray(adjustments)) return rows;
  for (const item of adjustments) {
    if (!item || typeof item !== 'object') continue;
    const row = item as { placement?: string; mode?: string; value?: unknown };
    if (!row.placement || !(row.placement in rows)) continue;
    const mode = PLACEMENT_MODE_OPTIONS.some((option) => option.id === row.mode)
      ? (row.mode as PlacementMode)
      : 'SET';
    rows[row.placement] = { mode, value: row.value == null ? '' : String(row.value) };
  }
  return rows;
}

function changeSummary(kind: Kind, item: HourlyBudgetListItem): string {
  if (kind === 'bid') return summarizeBidAction(item.action as BidDaypartingAction | null);
  return summarizePlacementAction(
    item.action as { adjustments: Array<{ placement: 'TOP_OF_SEARCH' | 'REST_OF_SEARCH' | 'PRODUCT_PAGES'; mode: PlacementMode; value: number }> } | null
  );
}

export function BidPlacementSection({ profileId, kind }: { profileId: string; kind: Kind }) {
  const copy = COPY[kind];
  const qc = useQueryClient();
  const { showToast } = useToast();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'true' | 'false'>('all');
  const [productFilter, setProductFilter] = useState<'all' | 'SP' | 'SB'>('all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorItem, setEditorItem] = useState<HourlyBudgetListItem | null>(null);
  const [scopeType, setScopeType] = useState<DaypartingScopeType>('campaigns');
  const [selectedCampaigns, setSelectedCampaigns] = useState<AutomationCampaignOption[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string | null>(null);
  const [slots, setSlots] = useState(emptyWeeklySlots);
  const [active, setActive] = useState(true);
  const [bidMethod, setBidMethod] = useState<BidMethod>('increase_pct');
  const [bidValue, setBidValue] = useState('');
  const [placementRows, setPlacementRows] = useState<PlacementRows>(emptyPlacementRows);
  const [formError, setFormError] = useState<string | null>(null);
  const [hourMessage, setHourMessage] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<HourlyBudgetListItem | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const { page, pageSize, setPage, setPageSize, offset } = useServerPagination(
    `${kind}|${search.trim()}|${productFilter}|${activeFilter}`
  );

  const listFilters = {
    q: search.trim() || undefined,
    active: activeFilter === 'all' ? undefined : activeFilter === 'true',
    product: productFilter === 'all' ? undefined : productFilter,
    kind,
    limit: pageSize,
    offset,
  };

  const listQ = useQuery({
    queryKey: profileHourlyBudgetsQueryKey(profileId, listFilters),
    queryFn: async () => {
      const res = await campaignsApi.listHourlyBudgets(profileId, listFilters);
      if (!res.success) throw new Error(`Failed to list ${copy.noun}`);
      return res.data ?? { items: [], total: 0 };
    },
    enabled: Boolean(profileId),
    placeholderData: keepPreviousData,
  });

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const filtersActive = Boolean(listFilters.q || listFilters.active != null || listFilters.product);
  const showEmptyState = !listQ.isLoading && !listQ.error && total === 0 && !filtersActive;

  useEffect(() => {
    if (!listQ.data || listQ.isFetching) return;
    const pages = totalPages(total, pageSize);
    if (page > pages) setPage(pages);
  }, [listQ.data, listQ.isFetching, total, pageSize, page, setPage]);

  const configuredQ = useQuery({
    queryKey: profileHourlyBudgetsQueryKey(profileId, { kind }),
    queryFn: async () => {
      const res = await campaignsApi.listHourlyBudgets(profileId, { kind });
      if (!res.success) throw new Error(`Failed to list ${copy.noun}`);
      return res.data ?? { items: [], total: 0 };
    },
    enabled: Boolean(profileId) && editorOpen,
  });

  const configuredIds = useMemo(() => {
    const allItems = configuredQ.data?.items ?? items;
    const skipCurrent = new Set(
      editorItem ? hourlyBudgetCampaigns(editorItem).map((campaign) => campaign.campaign_id) : []
    );
    return configuredHourlyBudgetCampaignIds(allItems).filter((id) => !skipCurrent.has(id));
  }, [configuredQ.data?.items, items, editorItem]);

  const entitiesQ = useQuery({
    queryKey: ['automation-entities', profileId, 'dayparting', kind],
    queryFn: () =>
      automationApi
        .listEntities(profileId, { entity_type: 'campaign', ad_types: 'SP,SB' })
        .then((response) =>
          mapEntityApiItems(response.data?.items ?? []).filter((option) => {
            const product = option.adProduct.toUpperCase();
            return product === 'SP' || product === 'SB';
          })
        ),
    enabled: Boolean(profileId) && editorOpen,
  });

  const portfoliosQ = useQuery({
    queryKey: ['ads-portfolios', profileId, 'dayparting', kind],
    queryFn: async () => {
      const res = await adsApi.listPortfolios(profileId);
      if (!res.success) throw new Error('Failed to list portfolios');
      return parsePortfolios(res.data);
    },
    enabled: Boolean(profileId) && editorOpen,
  });

  const pickerOptions = entitiesQ.data ?? [];
  const previewCampaigns = useMemo(
    () => filterCampaignsByPortfolio(pickerOptions, selectedPortfolioId),
    [pickerOptions, selectedPortfolioId]
  );

  const hasScope =
    scopeType === 'campaigns' ? selectedCampaigns.length > 0 : Boolean(selectedPortfolioId);
  const excludeGroupId = editorItem ? hourlyBudgetGroupId(editorItem) : null;
  const previewRequest = useMemo((): FlagPreviewRequest | null => {
    if (!editorOpen || !hasScope) return null;
    return {
      domain: 'dayparting',
      kind,
      source_type: scopeType,
      ...(scopeType === 'campaigns'
        ? {
            campaign_refs: selectedCampaigns.map((campaign) => ({
              campaign_id: campaign.campaignId,
              product: campaign.adProduct,
            })),
          }
        : { portfolio_id: selectedPortfolioId }),
      ...(excludeGroupId ? { exclude_group_id: excludeGroupId } : {}),
    };
  }, [editorOpen, excludeGroupId, hasScope, kind, scopeType, selectedCampaigns, selectedPortfolioId]);

  const [debouncedPreviewRequest, setDebouncedPreviewRequest] = useState(previewRequest);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedPreviewRequest(previewRequest), 250);
    return () => window.clearTimeout(timer);
  }, [previewRequest]);

  const flagsPreviewQ = useQuery({
    queryKey: ['automation-flags-preview', profileId, debouncedPreviewRequest],
    queryFn: async () => {
      if (!debouncedPreviewRequest) return [];
      const res = await flagsApi.preview(profileId, debouncedPreviewRequest);
      if (!res.success) throw new Error('Failed to preview flags');
      return parseAutomationFlags(res.data?.flags);
    },
    enabled: Boolean(profileId && debouncedPreviewRequest),
  });
  const previewFlags =
    debouncedPreviewRequest === previewRequest ? (flagsPreviewQ.data ?? []) : [];

  const openCreate = () => {
    setEditorItem(null);
    setScopeType('campaigns');
    setSelectedCampaigns([]);
    setSelectedPortfolioId(null);
    setSlots(emptyWeeklySlots());
    setActive(true);
    setBidMethod('increase_pct');
    setBidValue('');
    setPlacementRows(emptyPlacementRows());
    setFormError(null);
    setHourMessage(null);
    setEditorOpen(true);
  };

  const openEdit = (item: HourlyBudgetListItem) => {
    setEditorItem(item);
    setScopeType(item.source_type === 'portfolio' ? 'portfolio' : 'campaigns');
    setSelectedPortfolioId(item.portfolio?.portfolioId ?? null);
    setSelectedCampaigns(
      hourlyBudgetCampaigns(item).map((campaign) => ({
        campaignId: campaign.campaign_id,
        entityRef: campaign.campaign_id,
        name: campaign.name,
        adProduct: campaign.campaign_type,
        state: campaign.state,
      }))
    );
    setSlots(
      expandWindowsToWeeklySlots(
        (item.base_windows ?? []).map((window, index) => ({
          clientId: window.id ?? `base-${index}`,
          start_time_local: window.start_time_local,
          end_time_local: window.end_time_local,
          days_of_week: window.days_of_week,
        })),
        []
      )
    );
    setActive(item.active);
    const bid = bidFormFromAction(item.action);
    setBidMethod(bid.method);
    setBidValue(bid.value);
    setPlacementRows(placementRowsFromAction(item.action));
    setFormError(null);
    setHourMessage(null);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditorItem(null);
    setFormError(null);
  };

  const toggleHour = (day: WeekdayIndex, hour: number) => {
    setSlots((prev) => {
      const current = prev[day] ?? {};
      if (current[hour] != null) {
        const next = { ...current };
        delete next[hour];
        return { ...prev, [day]: next };
      }
      if (!canToggleHourOn(current, hour)) return prev;
      return { ...prev, [day]: { ...current, [hour]: '1' } };
    });
    const current = slots[day] ?? {};
    setHourMessage(
      current[hour] == null && !canToggleHourOn(current, hour)
        ? `At most 12 separate hour ranges allowed on ${WEEKDAYS[day].long}`
        : null
    );
  };

  const copyDay = (sourceDay: WeekdayIndex, targetDays: WeekdayIndex[]) => {
    const source = slots[sourceDay] ?? {};
    setSlots((prev) => {
      const next = { ...prev };
      for (const day of targetDays) next[day] = { ...source };
      return next;
    });
  };

  const clearDay = (day: WeekdayIndex) => {
    setSlots((prev) => ({ ...prev, [day]: {} }));
    setHourMessage(null);
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!hasScope) throw new Error('Choose campaigns or a portfolio.');
      if (selectedHourCount(slots as Record<number, HourSlotMap>) === 0) {
        throw new Error('Paint at least one hour.');
      }
      const actionError =
        kind === 'bid' ? validateBidAction(bidMethod, bidValue) : validatePlacementRows(placementRows);
      if (actionError) throw new Error(actionError);
      const action =
        kind === 'bid'
          ? { method: bidMethod, value: Number(bidValue) }
          : placementActionFromRows(placementRows);
      const body = buildBidPlacementGroupPayload({
        kind,
        active,
        slots,
        action,
        sourceType: scopeType,
        campaignRefs: selectedCampaigns.map((campaign) => ({
          campaign_id: campaign.campaignId,
          product: (campaign.adProduct.toUpperCase() === 'SB' ? 'SB' : 'SP') as CampaignAdProduct,
        })),
        portfolioId: selectedPortfolioId,
      });
      const groupId = editorItem ? hourlyBudgetGroupId(editorItem) : null;
      const res = groupId
        ? await campaignsApi.updateHourlyBudgetGroup(profileId, groupId, body)
        : await campaignsApi.createHourlyBudgetGroup(profileId, body);
      if (!res.success) throw new Error(`Failed to save ${copy.noun}`);
    },
    onSuccess: () => {
      showToast(`${kind === 'bid' ? 'Bid' : 'Placement'} schedule saved`, 'success');
      closeEditor();
      void qc.invalidateQueries({ queryKey: ['profile-hourly-budgets', profileId] });
    },
    onError: (error: unknown) => {
      setFormError(apiErrorMessage(error, `Failed to save ${copy.noun}`));
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (item: HourlyBudgetListItem) => {
      const groupId = hourlyBudgetGroupId(item);
      if (!groupId) throw new Error('This schedule is not a group and cannot be removed here.');
      const res = await campaignsApi.deleteHourlyBudgetGroup(profileId, groupId);
      if (!res.success) throw new Error(`Failed to remove ${copy.noun}`);
    },
    onSuccess: () => {
      showToast(`${kind === 'bid' ? 'Bid' : 'Placement'} schedule removed`, 'success');
      setRemoveTarget(null);
      setRemoveError(null);
      void qc.invalidateQueries({ queryKey: ['profile-hourly-budgets', profileId] });
    },
    onError: (error: unknown) => {
      setRemoveError(apiErrorMessage(error, `Failed to remove ${copy.noun}`));
    },
  });

  return (
    <>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="rounded border border-border px-3 py-1.5 text-sm"
            placeholder="Search campaigns"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search campaigns"
          />
          <select
            className="rounded border border-border bg-surface px-3 py-1.5 text-sm"
            value={productFilter}
            onChange={(event) => setProductFilter(event.target.value as 'all' | 'SP' | 'SB')}
            aria-label="Filter by product"
          >
            <option value="all">SP and SB</option>
            <option value="SP">SP</option>
            <option value="SB">SB</option>
          </select>
          <select
            className="rounded border border-border bg-surface px-3 py-1.5 text-sm"
            value={activeFilter}
            onChange={(event) => setActiveFilter(event.target.value as 'all' | 'true' | 'false')}
            aria-label="Filter by active"
          >
            <option value="all">All statuses</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <Button className="ml-auto" onClick={openCreate}>
            <Plus className="mr-1 h-4 w-4" />
            {copy.add}
          </Button>
        </div>

        {listQ.isLoading ? (
          <Spinner />
        ) : listQ.error ? (
          <p className="text-sm text-error">{(listQ.error as Error).message}</p>
        ) : showEmptyState ? (
          <div className="rounded-xl border border-border bg-surface px-6 py-16 text-center">
            <p className="text-base font-medium text-foreground">{copy.empty}</p>
            <p className="mt-1 text-sm text-foreground-muted">
              Paint the hours, then set one {kind === 'bid' ? 'bid' : 'placement'} change for those
              hours.
            </p>
            <Button className="mt-5" onClick={openCreate}>
              <Plus className="mr-1 h-4 w-4" />
              {copy.add}
            </Button>
          </div>
        ) : total === 0 ? (
          <p className="py-8 text-center text-sm text-foreground-muted">
            No campaigns match these filters.
          </p>
        ) : (
          <>
            <TableWrap>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Scope</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Painted hours</TableHead>
                    <TableHead>{copy.change}</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <tbody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">
                        <span className="inline-flex flex-wrap items-center gap-2">
                          {hourlyBudgetScopeLabel(item)}
                          <FlagBadge flags={parseAutomationFlags(item.flags)} />
                        </span>
                      </TableCell>
                      <TableCell>{hourlyBudgetProductsLabel(item)}</TableCell>
                      <TableCell>
                        <DaypartingStatus item={item} />
                      </TableCell>
                      <TableCell className="text-sm text-foreground-muted">
                        {formatOperatingWindows(item.base_windows, item.schedule_mode)}
                      </TableCell>
                      <TableCell className="text-sm">{changeSummary(kind, item)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            className="rounded p-1 text-sm hover:bg-surface-muted"
                            onClick={() => openEdit(item)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="rounded p-1 text-sm text-error-ink hover:bg-surface-muted"
                            onClick={() => {
                              setRemoveError(null);
                              setRemoveTarget(item);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            <WorkspaceTablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              disabled={listQ.isFetching}
            />
          </>
        )}
      </div>

      {editorOpen ? (
        <div
          className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/50 p-4"
          onClick={closeEditor}
        >
          <div
            className="relative w-full max-w-7xl overflow-hidden rounded-xl border border-border bg-surface shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
              <h2 className="text-base font-semibold text-foreground">
                {editorItem ? 'Edit' : 'Add'} {copy.noun}
              </h2>
              <button
                type="button"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-foreground-muted hover:bg-page hover:text-foreground"
                onClick={closeEditor}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <DaypartingScopePicker
                scopeType={scopeType}
                onScopeTypeChange={(next) => {
                  setScopeType(next);
                  if (next === 'campaigns') setSelectedPortfolioId(null);
                  else setSelectedCampaigns([]);
                }}
                campaignOptions={pickerOptions}
                selectedCampaigns={selectedCampaigns}
                onCampaignsChange={setSelectedCampaigns}
                campaignsLoading={entitiesQ.isLoading}
                portfolios={portfoliosQ.data ?? []}
                selectedPortfolioId={selectedPortfolioId}
                onPortfolioChange={setSelectedPortfolioId}
                portfoliosLoading={portfoliosQ.isLoading}
                previewCampaigns={previewCampaigns}
                conflictCampaignIds={configuredIds}
                conflictLabel={`Has ${copy.noun}`}
                conflictNoun={copy.noun}
                scopeNote="The same change applies to each campaign."
              />
              {previewFlags.length > 0 ? <FlagCallout flags={previewFlags} /> : null}
              <label className="inline-flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(event) => setActive(event.target.checked)}
                />
                Active
              </label>
              {kind === 'bid' ? (
                <div className="flex flex-wrap items-end gap-2">
                  <label className="text-sm text-foreground">
                    Bid change
                    <select
                      className="mt-1 block rounded border border-border bg-surface px-3 py-1.5 text-sm"
                      value={bidMethod}
                      onChange={(event) => setBidMethod(event.target.value as BidMethod)}
                      aria-label="Bid change"
                    >
                      {BID_METHOD_OPTIONS.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-sm text-foreground">
                    Value
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={bidValue}
                      onChange={(event) => setBidValue(event.target.value)}
                      aria-label="Bid value"
                      className="mt-1 block w-28 rounded border border-border bg-surface px-3 py-1.5 text-sm"
                    />
                  </label>
                </div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-3">
                  {PLACEMENT_FIELD_OPTIONS.map((field) => {
                    const row = placementRows[field.id];
                    return (
                      <div key={field.id} className="rounded border border-border p-3">
                        <p className="mb-2 text-sm font-medium text-foreground">{field.label}</p>
                        <select
                          className="mb-2 w-full rounded border border-border bg-surface px-2 py-1.5 text-sm"
                          value={row.mode}
                          aria-label={`${field.label} mode`}
                          onChange={(event) =>
                            setPlacementRows((prev) => ({
                              ...prev,
                              [field.id]: { ...prev[field.id], mode: event.target.value as PlacementMode },
                            }))
                          }
                        >
                          {PLACEMENT_MODE_OPTIONS.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          step="1"
                          value={row.value}
                          aria-label={`${field.label} value`}
                          placeholder="Leave blank to skip"
                          onChange={(event) =>
                            setPlacementRows((prev) => ({
                              ...prev,
                              [field.id]: { ...prev[field.id], value: event.target.value },
                            }))
                          }
                          className="w-full rounded border border-border bg-surface px-2 py-1.5 text-sm"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
              <HourlyBudgetWeekGrid
                selectionOnly
                slotsByDay={slots}
                amountsByDay={emptyWeeklyAmounts()}
                timezoneLabel="IST (India Standard Time)"
                validationMessage={hourMessage}
                onToggleHour={toggleHour}
                onMultiplierChange={() => {}}
                onAmountChange={() => {}}
                onCopyDay={copyDay}
                onClearDay={clearDay}
              />
              {formError ? <p className="text-sm text-error">{formError}</p> : null}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={closeEditor}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={saveMut.isPending || hasErrorFlags(previewFlags)}
                  onClick={() => {
                    setFormError(null);
                    saveMut.mutate();
                  }}
                >
                  {saveMut.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {removeTarget ? (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
          onClick={deleteMut.isPending ? undefined : () => setRemoveTarget(null)}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-surface p-5 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="mb-2 text-base font-semibold text-foreground">Remove {copy.noun}?</h3>
            <p className="mb-4 text-sm text-foreground-muted">
              Open changes are written back before this schedule is removed.
            </p>
            {removeError ? <p className="mb-3 text-sm text-error">{removeError}</p> : null}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={deleteMut.isPending}
                onClick={() => setRemoveTarget(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={deleteMut.isPending}
                onClick={() => deleteMut.mutate(removeTarget)}
              >
                {deleteMut.isPending ? 'Removing…' : 'Remove'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
